import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Mail } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { TournamentEmailEligibility } from '@/components/tournament/email-eligibility'
import { AccountEmailError, accountEmailRequest } from '@/lib/account-email'
import type { AccountEmailStatus } from '@/lib/account-email'
import { useAuth } from '@/lib/auth'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { useI18n } from '@/lib/i18n'
import { returnTargetFrom, safeReturnTarget, signInUrlReturningTo } from '@/lib/return-target'
import { useTRPC } from '@/lib/trpc'
import { getPathLanguagePrefix, withLanguagePath } from '@/lib/url-language-utils'

export function VerifyEmailPage() {
  const { actor, authResolved } = useAuth()
  const { t, language } = useI18n()
  const location = useLocation()
  const [params] = useSearchParams()
  const queryClient = useQueryClient()
  const trpc = useTRPC()
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ returnTarget: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const authenticated = !!actor && !actor.isAnonymous
  const token = params.get('token')
  const status = useQuery({
    queryKey: ['account-email-status', actor?.id],
    queryFn: () => accountEmailRequest<AccountEmailStatus>('account-email-status'),
    enabled: authResolved && authenticated,
    refetchOnWindowFocus: true,
  })
  const returnTarget = safeReturnTarget(result?.returnTarget ?? params.get('redirect'), '/new')
  const returnHref = getPathLanguagePrefix(returnTarget) ? returnTarget : withLanguagePath(returnTarget, language)
  const verified = result !== null || status.data?.state === 'EMAIL_VERIFIED'
  const refresh = async () => {
    await status.refetch({ throwOnError: true })
    await Promise.all([
      queryClient.invalidateQueries(trpc.userRouter.getUserInfo.queryFilter()),
      queryClient.invalidateQueries(trpc.tournamentRouter.getViewer.queryFilter()),
    ])
  }
  const complete = async () => {
    setBusy(true)
    setError(null)
    try {
      const completed = await accountEmailRequest<{ returnTarget: string }>('complete-account-email-verification', { token: token ?? '' })
      setResult(completed)
      await refresh()
    }
    catch (failure) {
      setError(failure instanceof AccountEmailError && failure.code !== 'UNKNOWN'
        ? 'accountEmail.invalidLink'
        : 'forum.errorGeneric')
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <main className="min-h-screen px-4 pb-10 pt-28">
      <div className="mx-auto w-full max-w-lg rounded-2xl border border-[#d5a545] bg-gradient-to-br from-[#15323e] via-[#092735] to-[#061d2b] p-6 text-center text-[#fff4d9] sm:p-8">
        <Mail className="mx-auto mb-4 size-10 text-[#ffd777]" aria-hidden="true" />
        <h1 className="text-2xl font-semibold">{t('accountEmail.verifyAction')}</h1>
        <div role="status" data-testid="verify-email-status" className="mt-4 text-sm">
          {!authResolved || (status.isPending && authenticated)
            ? t('forum.loading')
            : verified
              ? t('accountEmail.verified')
              : error
                ? t(error)
                : status.isError
                  ? t('forum.errorGeneric')
                  : t(authenticated ? 'accountEmail.pendingTitle' : token ? 'tournament.loginBody' : 'nav.signIn')}
        </div>
        {authResolved && !authenticated && (
          <Button asChild className={`${gameStartButtonClassName} mt-5 min-h-12 w-full`}>
            <Link data-testid="verify-email-signin" to={signInUrlReturningTo(returnTargetFrom(location))}>
              {t('nav.signIn')}
            </Link>
          </Button>
        )}
        {authResolved && authenticated && token && !verified && (
          <Button
            data-testid="verify-email-complete"
            className={`${gameStartButtonClassName} mt-5 min-h-12 w-full`}
            disabled={busy}
            onClick={() => { void complete() }}
          >
            {busy && <Loader2 className="me-2 size-4 animate-spin" aria-hidden="true" />}
            {t('accountEmail.verifyAction')}
          </Button>
        )}
        {authResolved && authenticated && !verified && (!token || error) && status.data && (
          <TournamentEmailEligibility
            status={status.data}
            refresh={refresh}
            returnTarget={returnTarget}
            variant="game"
          />
        )}
        {authResolved && authenticated && status.isError && (
          <Button variant="outline" className="mt-4 min-h-11 w-full" onClick={() => { void refresh().catch(() => {}) }}>
            {t('play.refreshStatus')}
          </Button>
        )}
        <Button asChild variant="outline" className="mt-4 min-h-11 w-full border-[#d5a545] bg-transparent hover:bg-white/10">
          <a href={returnHref} data-testid="verify-email-return">{t(returnTarget.startsWith('/tournaments') ? 'accountEmail.backToTournament' : 'common.back')}</a>
        </Button>
      </div>
    </main>
  )
}
